/**
 * EntitlementContext — current entitlements (capability + quota + add-on).
 * Runs alongside FeatureContext while remaining feature-policy surfaces migrate.
 *
 * Usage:
 *   const { hasCapability, hasAddOn, getQuota } = useEntitlements();
 *
 * @userstory US-ENT-01, US-ENT-02, US-ENT-03, US-CPF-SRCH-07, US-CPF-SRCH-08
 */
import React, {
	createContext,
	useState,
	useCallback,
	useMemo,
	type ReactNode,
} from 'react';
import entitlementService, { type QuotaState } from '../services/api/entitlementService';

// ============================================================================
// Types
// ============================================================================

export interface QuotaInfo {
	used: number;
	/** null = unlimited */
	limit: number | null;
	remaining: number;
	resetAt: string; // ISO-8601
}

export interface EntitlementContextValue {
	/** Returns true if the org's active plan includes the capability. */
	hasCapability: (key: string) => boolean;
	/** Returns true if the org has the add-on active. */
	hasAddOn: (key: string) => boolean;
	/** Returns cached quota state, or null if not loaded. */
	getQuota: (key: string) => QuotaInfo | null;
	/** Fetches/refreshes quota state from the API and caches it. */
	loadQuota: (organizationId: string, key: string) => Promise<QuotaInfo>;
	/** Optimistically increments the local quota counter by 1. */
	incrementQuotaUsed: (key: string) => void;
	/** Pushes server quota state into the cache (called from auth-sync or explicit load). */
	setQuotaState: (state: QuotaState) => void;
	/** Pushes capability flags into the cache (called from auth-sync). */
	setCapabilities: (capabilities: Record<string, boolean>) => void;
	/** Pushes add-on flags into the cache (called from auth-sync). */
	setAddOns: (addons: Record<string, boolean>) => void;
	/** Clears all cached entitlement state (on logout). */
	clearEntitlements: () => void;
}

// ============================================================================
// Context
// ============================================================================

const defaultValue: EntitlementContextValue = {
	hasCapability: () => false,
	hasAddOn: () => false,
	getQuota: () => null,
	loadQuota: async () => ({ used: 0, limit: null, remaining: 0, resetAt: '' }),
	incrementQuotaUsed: () => {},
	setQuotaState: () => {},
	setCapabilities: () => {},
	setAddOns: () => {},
	clearEntitlements: () => {},
};

export const EntitlementContext = createContext<EntitlementContextValue>(defaultValue);

// ============================================================================
// Provider
// ============================================================================

interface EntitlementProviderProps {
	children: ReactNode;
}

/**
 * Provider for entitlement state.
 * Mount once near the app root (alongside FeatureProvider).
 *
 * @userstory US-ENT-01, US-ENT-02, US-ENT-03
 */
export const EntitlementProvider: React.FC<EntitlementProviderProps> = ({ children }) => {
	const [, setCapabilitiesState] = useState<Record<string, boolean>>({});
	const [addOns, setAddOnsState] = useState<Record<string, boolean>>({});
	const [quotas, setQuotasState] = useState<Record<string, QuotaInfo>>({});

	// AI-credits-only baseline (BR-FG-OPEN-01): every capability resolves as available; add-ons read
	// the real cache (Freelance Management stays parked/hidden); the only enforced limit is the
	// AI credits quota (org.ai_credits_per_month), surfaced through getQuota.
	const hasCapability = useCallback(() => true, []);
	const hasAddOn = useCallback((key: string) => addOns[key] ?? false, [addOns]);
	const getQuota = useCallback(
		(key: string): QuotaInfo | null => quotas[key] ?? null,
		[quotas],
	);

	const loadQuota = useCallback(
		async (organizationId: string, key: string): Promise<QuotaInfo> => {
			const state = await entitlementService.getQuotaState(organizationId, key);
			const info: QuotaInfo = {
				used: state.used,
				limit: state.limit,
				remaining: state.remaining,
				resetAt: state.resetAt,
			};
			setQuotasState((prev) => ({ ...prev, [key]: info }));
			return info;
		},
		[],
	);

	const incrementQuotaUsed = useCallback((key: string) => {
		setQuotasState((prev) => {
			const current = prev[key];
			if (!current) return prev;
			const newUsed = current.used + 1;
			const newRemaining =
				current.limit !== null ? Math.max(0, current.limit - newUsed) : current.remaining;
			return { ...prev, [key]: { ...current, used: newUsed, remaining: newRemaining } };
		});
	}, []);

	const setQuotaState = useCallback((state: QuotaState) => {
		setQuotasState((prev) => ({
			...prev,
			[state.quotaKey]: {
				used: state.used,
				limit: state.limit,
				remaining: state.remaining,
				resetAt: state.resetAt,
			},
		}));
	}, []);

	const setCapabilities = useCallback((caps: Record<string, boolean>) => {
		setCapabilitiesState(caps);
	}, []);

	const setAddOns = useCallback((aos: Record<string, boolean>) => {
		setAddOnsState(aos);
	}, []);

	const clearEntitlements = useCallback(() => {
		setCapabilitiesState({});
		setAddOnsState({});
		setQuotasState({});
	}, []);

	const value = useMemo<EntitlementContextValue>(
		() => ({
			hasCapability,
			hasAddOn,
			getQuota,
			loadQuota,
			incrementQuotaUsed,
			setQuotaState,
			setCapabilities,
			setAddOns,
			clearEntitlements,
		}),
		[
			hasCapability,
			hasAddOn,
			getQuota,
			loadQuota,
			incrementQuotaUsed,
			setQuotaState,
			setCapabilities,
			setAddOns,
			clearEntitlements,
		],
	);

	return <EntitlementContext.Provider value={value}>{children}</EntitlementContext.Provider>;
};
